import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { ApiError } from "@/lib/api";

import { useAuth } from "./AuthContext";

// Phase 2a customer-accounts FR-6 — landing page for the
// /login/magic?token=... link in the email. Auto-consumes the token on
// mount and redirects to /account, or surfaces "expired / used" if the
// server rejects.
export function MagicLinkConsumePage(): JSX.Element {
  const [params] = useSearchParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const consumed = useRef(false);
  const [status, setStatus] = useState<"working" | "error">("working");

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;

    const token = params.get("token") ?? "";
    if (!token) {
      setStatus("error");
      return;
    }
    auth
      .consumeMagicLink(token)
      .then(() => navigate("/account", { replace: true }))
      .catch((err) => {
        if (err instanceof ApiError) {
          setStatus("error");
        } else {
          setStatus("error");
        }
      });
  }, [auth, navigate, params]);

  if (status === "working") {
    return (
      <section className="mx-auto max-w-md space-y-3 text-center" aria-live="polite">
        <h1 className="text-2xl font-semibold text-brand-black">Signing you in…</h1>
        <p className="text-sm text-zinc-600">Hold on a moment.</p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-md space-y-4">
      <Alert variant="destructive" role="alert">
        This sign-in link has expired or already been used.
      </Alert>
      <p className="text-sm text-zinc-600">
        Head back to the sign-in screen to request a fresh link, or use your password.
      </p>
      <Link to="/sign-in" className="inline-block text-brand-red underline">
        Back to sign in
      </Link>
    </section>
  );
}
